import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { Landmark, Plus, LogOut, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useBookingDialog } from "@/context/BookingDialogContext";

const NAV = [
  { to: "/", label: "Dashboard", id: "dashboard", end: true },
  { to: "/strutture", label: "Strutture", id: "facilities" },
  { to: "/calendario", label: "Calendario", id: "calendar" },
  { to: "/prenotazioni", label: "Registro pratiche", id: "bookings" },
  { to: "/personale", label: "Personale", id: "staff", admin: true },
];

const UserMenu = ({ user, logout }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-white/10 transition-colors">
        <span className="grid h-8 w-8 place-items-center rounded-full bg-[#8B1E2B] text-sm font-semibold text-white">
          {user.name?.[0]?.toUpperCase()}
        </span>
        <span className="hidden lg:block leading-tight">
          <span className="block text-sm font-semibold text-white">{user.name}</span>
          <span className="block text-[11px] uppercase tracking-wider text-slate-400">{user.role === "admin" ? "Amministratore" : "Operatore"}</span>
        </span>
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56">
      <DropdownMenuLabel>
        <span className="block">{user.name}</span>
        <span className="block text-xs font-normal text-slate-500">{user.email}</span>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-red-700">
        <LogOut className="mr-2 h-4 w-4" /> Esci
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const NavItems = ({ isAdmin }) =>
  NAV.filter((n) => !n.admin || isAdmin).map((n) => (
    <NavLink
      key={n.id}
      to={n.to}
      end={n.end}
      data-testid={`nav-${n.id}-link`}
      className={({ isActive }) =>
        `whitespace-nowrap border-b-2 px-1 py-3 text-sm font-semibold transition-colors ${
          isActive ? "border-[#C9A45C] text-white" : "border-transparent text-slate-400 hover:text-white"
        }`
      }
    >
      {n.label}
    </NavLink>
  ));

export default function Layout() {
  const { user, logout } = useAuth();
  const { openNew } = useBookingDialog();
  const location = useLocation();

  if (user === null) return <div className="grid min-h-screen place-items-center eyebrow" data-testid="auth-loading">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  return (
    <div className="min-h-screen bg-[#F8F6F2]">
      <header className="sticky top-0 z-40 bg-[#1E293B] text-white shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8 h-16">
          <NavLink to="/" className="flex items-center gap-3" data-testid="brand-link">
            <span className="grid h-10 w-10 place-items-center rounded-md border border-[#C9A45C]/50 bg-[#8B1E2B]">
              <Landmark className="h-5 w-5 text-[#F3E3BF]" />
            </span>
            <span className="leading-tight">
              <span className="block font-serif text-xl font-semibold">Comune di Subiaco</span>
              <span className="hidden sm:block text-[11px] uppercase tracking-[0.18em] text-slate-400">Spazi comunali in concessione</span>
            </span>
          </NavLink>
          <nav className="ml-8 hidden md:flex items-center gap-6"><NavItems isAdmin={user.role === "admin"} /></nav>
          <div className="ml-auto flex items-center gap-2">
            <Button onClick={() => openNew()} data-testid="new-booking-button" className="bg-[#8B1E2B] hover:bg-[#701621] rounded-full px-4">
              <Plus className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Nuova prenotazione</span>
            </Button>
            <UserMenu user={user} logout={logout} />
          </div>
        </div>
        <nav className="md:hidden flex gap-5 overflow-x-auto no-scrollbar px-4 border-t border-white/10"><NavItems isAdmin={user.role === "admin"} /></nav>
      </header>
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        <Outlet />
      </main>
    </div>
  );
}
